import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  IconAlertTriangle, IconBuildingStore, IconCalendarPlus, IconCalendarTime, IconChevronDown, IconChevronLeft, IconDots, IconExternalLink, IconFileDollar, IconFileText, IconInfoCircle, IconMinus,
  IconPackage, IconPlus, IconRoute, IconSearch, IconSend, IconShip, IconTag, IconUsers, IconCheck, IconBriefcase,
} from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Drawer, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { byTab, dayOffset, formatPrice, searchQuotes, statusCounts, statusOf, CALC_LABEL, NATURE_LABEL } from './logic';
import { LOCAL_PER_CONT, QUICK_CUSTOMERS, QUICK_PODS, QUICK_TYPES, hasPricingTicket, quickOptions, quickTotal, viewLines, type QuickType } from './compact';
import type { Quote, QuoteStatus } from './types';
import { STATUS_DOT } from './QuoteDock';
import './quoteCompact.i18n';
import s from './quoteCompact.module.css';

// Mint-Quote-iPad (danh sách + chi tiết) · Mint-Quote-Phone (danh sách + bảng Báo giá nhanh). Chỉ đọc; nút ghi báo toast.
// Giá bán là giá gửi khách; Margin / giá mua không hiện (chưa có quyền `money`): giá dưới sàn chỉ báo "Chờ duyệt".

type Tab = 'all' | QuoteStatus | 'Live';
const STATUS_CLS: Record<QuoteStatus, string> = { 'Nháp': 'st0', 'Đã gửi': 'st1', 'Khách chấp nhận': 'st2', 'Hết hạn': 'st3', 'Đã thành Job': 'st4' };
const TABS_PAD: readonly { key: Tab; label: string }[] = [
  { key: 'all', label: 'Tất cả' }, { key: 'Nháp', label: 'Nháp' }, { key: 'Đã gửi', label: 'Đã gửi' }, { key: 'Khách chấp nhận', label: 'Chấp nhận' },
  { key: 'Hết hạn', label: 'Hết hạn' }, { key: 'Đã thành Job', label: 'Thành Job' }, { key: 'Live', label: 'Live' },
];
const PHONE_ORDER: readonly Tab[] = ['all', 'Đã gửi', 'Khách chấp nhận', 'Hết hạn', 'Nháp', 'Đã thành Job', 'Live'];
const TABS_PHONE = PHONE_ORDER.map((k) => TABS_PAD.find((t) => t.key === k)!);
/** Báo giá có trên board, theo thứ tự board: iPad 9 dòng, điện thoại 7 dòng (không có NRT181 và UKB190). */
const PAD_IDS = ['Q26FRSGNPUS200', 'Q26FDSGNPUS199', 'Q26ASGNNRT181', 'Q26FRSGNYOK198', 'Q26LDSGNSIN182', 'Q26FRSGNUKB193', 'Q26FRSGNUKB190', 'Q26FDSGNSHA189', 'Q26FRSGNTYO188'];
/** Dữ liệu mẫu của board iPad / điện thoại lệch bản desktop ở hai báo giá (ngày hiệu lực YOK198; trạng thái SIN182). */
const BOARD_PATCH: Readonly<Record<string, Partial<Quote>>> = { Q26FRSGNYOK198: { validDays: 2 }, Q26LDSGNSIN182: { status: 'Nháp' } };
const PHONE_SKIP = new Set(['Q26ASGNNRT181', 'Q26FRSGNUKB190']);

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

/** Giá bán trên thẻ: số nguyên không thêm số lẻ (38), giá theo kg giữ hai số lẻ (3,85). */
const priceText = (v: number): string => (Number.isInteger(v) ? v.toLocaleString('en-US') : formatPrice(v));

const Ab = ({ text }: { text: string }) => (
  <span className={s.ab} aria-hidden="true">
    {text.slice(0, 2).toUpperCase()}
  </span>
);

function StatusChip({ q }: { q: Quote }) {
  const st = statusOf(q);
  return (
    <span className={`${s.st} ${s[STATUS_CLS[st]]}`}>
      <span className={s.dot} style={{ background: STATUS_DOT[st] }} aria-hidden="true" />
      {st}
    </span>
  );
}

export function QuoteCompact({ phone }: { phone: boolean }) {
  const tx = useT();
  const toast = useToast();
  const { data, error, isLoading, isError, refetch } = useList('quote', { limit: 5000 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [swiped, setSwiped] = useState<string | null>(null);
  const [sheet, setSheet] = useState(false);

  const all = useMemo(() => PAD_IDS.filter((id) => !(phone && PHONE_SKIP.has(id))).flatMap((id) => { const r = data?.items.find((x) => x.id === id); return r ? [{ ...r, ...BOARD_PATCH[id] }] : []; }), [data, phone]);
  const filtered = useMemo(() => searchQuotes(all, q), [all, q]);
  const counts = statusCounts(filtered);
  const rows = useMemo(() => byTab(filtered, tab === 'all' ? null : tab), [filtered, tab]);

  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const ready = state === 'ready';

  const current = open ? all.find((r) => r.id === open) : undefined;
  if (!phone && current && ready) return <QuoteDetail q={current} index={all.indexOf(current)} onBack={() => setOpen(null)} />;

  const cnt = (k: Tab): number | undefined => (!ready ? undefined : k === 'all' ? filtered.length : k === 'Live' ? filtered.filter((r) => r.live).length : counts[k]);
  const items = (phone ? TABS_PHONE : TABS_PAD).map((t) => ({ key: t.key as string, label: t.label, n: cnt(t.key) }));

  return (
    <Page kind="table">
      <PageHeader
        title="Báo giá"
        actions={
          phone ? (
            <button type="button" className={s.tool} aria-label="Thêm · Góp ý" title="Thêm · Góp ý" onClick={() => toast({ msg: 'Góp ý chưa mở ở bản này' })}>
              <IconDots size={20} aria-hidden="true" />
            </button>
          ) : (
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {tx('qc.1')}
            </Button>
          )
        }
      />
      <div className={s.page}>
        <div className={s.tabsRow}>
          <Segmented label="Trạng thái báo giá" value={tab as string} onChange={(k) => setTab(k as Tab)} items={items} />
        </div>
        {!phone && (
          <label className={s.search}>
            <IconSearch size={17} aria-hidden="true" />
            <input aria-label="Tìm báo giá" placeholder="Số BG / khách / tuyến" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        )}
        <StateView
          state={ready && rows.length === 0 ? 'empty' : state}
          title={ready ? 'Không có báo giá khớp' : state === 'empty' ? 'Chưa có báo giá' : undefined}
          desc={ready ? 'Thử đổi từ khoá hoặc trạng thái.' : state === 'empty' && phone ? 'Bấm + để báo giá nhanh cho khách.' : undefined}
          icon={ready || state === 'empty' ? <IconFileText size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{tx('qc.2')}</Button> : undefined}
          skeleton={<Skeleton rows={5} />}
        >
          <section aria-label={phone ? undefined : 'Danh sách báo giá'}>
            <ul className={s.list}>
              {rows.map((r) =>
                phone ? (
                  <li key={r.id}>
                    <PhoneCard r={r} swiped={swiped === r.id} onSwipe={(v) => setSwiped(v ? r.id : null)} onOpen={() => toast({ msg: `Chi tiết ${r.id} chưa mở ở bản này` })} onExtend={() => toast({ msg: `Đã gia hạn ${r.id} thêm 7 ngày` })} />
                  </li>
                ) : (
                  <li key={r.id}>
                    <PadCard r={r} onOpen={() => setOpen(r.id)} />
                  </li>
                ),
              )}
            </ul>
          </section>
        </StateView>
      </div>
      {phone && ready && (
        <>
          <button type="button" className={s.fab} aria-label="Báo giá nhanh" onClick={() => setSheet(true)}>
            <IconPlus size={26} aria-hidden="true" />
          </button>
          <QuickSheet open={sheet} onOpenChange={setSheet} onSend={(msg) => { setSheet(false); toast({ msg }); }} />
        </>
      )}
    </Page>
  );
}

function PadCard({ r, onOpen }: { r: Quote; onOpen: () => void }) {
  const tx = useT();
  const tone = r.validDays < 0 ? s.err : r.validDays <= 7 ? s.warnTx : undefined;
  return (
    <button type="button" className={s.card} onClick={onOpen}>
      <span className={s.top}>
        <span className={s.no}>{r.id}</span>
        <StatusChip q={r} />
      </span>
      <span className={s.cus} style={{ display: 'block' }}>{r.customer}</span>
      <span className={s.mid}>
        <span className={s.chips}>
          <span className={s.meta} title={`Tuyến ${r.route}`}><IconRoute size={14} aria-hidden="true" />{r.route}</span>
          <span className={s.meta} title={`Dịch vụ ${r.kind}`}><IconPackage size={14} aria-hidden="true" />{r.kind}</span>
          <span className={s.meta} title={`Hãng ${r.carrier}`}><IconShip size={14} aria-hidden="true" />{r.carrier}</span>
        </span>
        <b className={s.sell}>{priceText(r.price)}</b>
      </span>
      <span className={s.foot}>
        <span className={`${s.meta} ${tone ?? ''}`} title={`Hiệu lực đến ${dayOffset(r.validDays)}`}>
          <IconCalendarTime size={13} aria-hidden="true" />
          {dayOffset(r.validDays)}
        </span>
        <span className={s.who} title={`Sales ${r.sale}`}>
          <Ab text={r.sale} />
          {r.sale}
          {r.under && (
            <span className={s.warnFlag} title="Giá dưới sàn — cần [SAL-MGR] duyệt">
              <IconAlertTriangle size={13} aria-hidden="true" />
              {tx('qc.3')}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

function PhoneCard({ r, swiped, onSwipe, onOpen, onExtend }: { r: Quote; swiped: boolean; onSwipe: (v: boolean) => void; onOpen: () => void; onExtend: () => void }) {
  const tx = useT();
  const [x0, setX0] = useState<number | null>(null);
  const [moved, setMoved] = useState(false);
  const tone = r.validDays < 0 ? s.err : r.validDays <= 7 ? s.warnTx : undefined;
  return (
    <div className={`${s.swipe} ${swiped ? s.swiped : ''}`}>
      <button type="button" className={s.extend} aria-label={`Gia hạn ${r.id}`} onClick={onExtend}>
        <IconCalendarPlus size={16} aria-hidden="true" />
        {tx('qc.4')}
      </button>
      <button
        type="button"
        className={s.card}
        onTouchStart={(e) => { setX0(e.touches[0]?.clientX ?? null); setMoved(false); }}
        onTouchEnd={(e) => {
          const dx = x0 == null ? 0 : (e.changedTouches[0]?.clientX ?? x0) - x0;
          setMoved(Math.abs(dx) > 10);
          if (dx < -50) onSwipe(true);
          else if (dx > 50) onSwipe(false);
          setX0(null);
        }}
        onClick={() => {
          if (moved) return setMoved(false);
          if (swiped) return onSwipe(false);
          onOpen();
        }}
      >
        <span className={s.top}>
          <span className={s.no}>{r.id}</span>
          <StatusChip q={r} />
        </span>
        <span className={s.cus} style={{ display: 'block' }}>{r.customer}</span>
        <span className={s.mid}>
          <span className={s.chips}>
            <span className={s.meta} title={`Tuyến ${r.route}`}><IconRoute size={14} aria-hidden="true" />{r.route}</span>
            <span className={s.meta} title={`Hãng ${r.carrier}`}><IconShip size={14} aria-hidden="true" />{r.carrier}</span>
          </span>
          <b className={s.sell}>{priceText(r.price)}</b>
        </span>
        <span className={s.foot}>
          <span className={`${s.meta} ${tone ?? ''}`} title={`Hiệu lực đến ${dayOffset(r.validDays)}`}>
            <IconCalendarTime size={13} aria-hidden="true" />
            {dayOffset(r.validDays)}
          </span>
          <span>{r.unit}</span>
        </span>
      </button>
    </div>
  );
}

/** Bảng trượt Báo giá nhanh: khách → tuyến → loại + số cont → phương án hãng → giá bán gợi ý. */
function QuickSheet({ open, onOpenChange, onSend }: { open: boolean; onOpenChange: (o: boolean) => void; onSend: (msg: string) => void }) {
  const tx = useT();
  const toast = useToast();
  const [cus, setCus] = useState(QUICK_CUSTOMERS[0]?.[0] ?? '');
  const [pod, setPod] = useState<string>('LAX');
  const [type, setType] = useState<QuickType>('40HC');
  const [n, setN] = useState(2);
  const [pick, setPick] = useState(0);
  const opts = quickOptions(cus, pod, type);
  const chosen = opts[pick] ?? opts[0];
  return (
    <Drawer open={open} onOpenChange={onOpenChange} title="Báo giá nhanh" description="Khách, tuyến, cont → giá bán gợi ý">
      <div className={s.q}>
        <div>
          <div className={s.qk}>{tx('qc.5')}</div>
          <div className={s.pills}>
            {QUICK_CUSTOMERS.map(([c]) => (
              <button key={c} type="button" className={s.pill} aria-pressed={cus === c} onClick={() => { setCus(c); setPick(0); }}>{c}</button>
            ))}
          </div>
        </div>
        <div>
          <div className={s.qk}>{tx('qc.6')}</div>
          <div className={s.pills}>
            {QUICK_PODS.map((p) => (
              <button key={p} type="button" className={s.pill} aria-pressed={pod === p} onClick={() => { setPod(p); setPick(0); }}>{p}</button>
            ))}
          </div>
        </div>
        <div className={s.cont}>
          <div className={s.pills} role="radiogroup" aria-label="Loại cont">
            {QUICK_TYPES.map((t) => (
              <button key={t} type="button" role="radio" className={s.pill} aria-checked={type === t} onClick={() => setType(t)}>{t}</button>
            ))}
          </div>
          <div className={s.step}>
            <button type="button" className={s.tool} aria-label="Bớt cont" onClick={() => setN((v) => Math.max(1, v - 1))}><IconMinus size={20} aria-hidden="true" /></button>
            <b>{n}</b>
            <button type="button" className={s.tool} aria-label="Thêm cont" onClick={() => setN((v) => v + 1)}><IconPlus size={20} aria-hidden="true" /></button>
          </div>
        </div>
        {opts.length === 0 && (
          <div className={s.none}>
            <b>{tx('qc.7')}</b>
            <Button icon={<IconSend size={16} aria-hidden="true" />} onClick={() => toast({ msg: 'Đã gửi hỏi giá · task PRC hạn 24 giờ LV' })}>{tx('qc.8')}</Button>
          </div>
        )}
        {opts.map((o, i) => (
          <button key={o.carrier} type="button" role="radio" className={s.opt} aria-checked={pick === i} onClick={() => setPick(i)}>
            <span>
              <b>{o.carrier}</b>
              <span style={{ display: 'flex', marginTop: 2 }}>
                <span className={s.meta} title={`Transit ${o.transit}`}><IconRoute size={13} aria-hidden="true" />{o.transit.split(' · ')[0]} · {o.transit.split(' · ')[1]}</span>
                <span className={s.meta} title={`ETD ${o.etd}`}><IconCalendarTime size={13} aria-hidden="true" />{o.etd}</span>
              </span>
            </span>
            <b>{o.sell.toLocaleString('en-US')} USD</b>
          </button>
        ))}
        {chosen && (
          <>
            <div className={s.total}>
              <span>Tổng {n}×{type} + local</span>
              <b>{quickTotal(chosen, n).toLocaleString('en-US')} USD</b>
            </div>
            <div className={s.qActs}>
              <Button variant="secondary" icon={<IconExternalLink size={16} aria-hidden="true" />} onClick={() => toast({ msg: 'Trang soạn đầy đủ: mở từ máy tính' })}>{tx('qc.9')}</Button>
              <Button variant="primary" icon={<IconSend size={16} aria-hidden="true" />} onClick={() => onSend(`Chưa mở ghi ở bản này · ${cus} ${pod} ${quickTotal(chosen, n).toLocaleString('en-US')} USD (local ${LOCAL_PER_CONT}/cont)`)}>{tx('qc.10')}</Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}

type Sec = 'price' | 'info' | 'inq' | 'hist';
const HINT: Record<Sec, string> = {
  price: 'Giá bán gửi khách. Margin chỉ hiện khi có quyền xem (money = view); không quyền thì chỉ báo “cần duyệt giá”.',
  info: 'Khách, người nhận, tuyến, dịch vụ, hãng / tàu và hiệu lực của báo giá.',
  inq: 'Phiếu hỏi giá nội bộ (Pricing, task PRC hạn 24 giờ làm việc) và hỏi giá đối tác / đại lý.',
  hist: 'Lịch sử báo giá: tạo, gửi, gia hạn, khách phản hồi.',
};

/** Trang chi tiết toàn màn (Mint-Quote-iPad-Detail): trái = phương án & giá · dòng · hoạt động; phải = thông tin chính · hỏi giá. */
function QuoteDetail({ q, index, onBack }: { q: Quote; index: number; onBack: () => void }) {
  const tx = useT();
  const toast = useToast();
  const nav = useNavigate();
  const [shut, setShut] = useState<Partial<Record<Sec, boolean>>>({});
  const [menu, setMenu] = useState(false);
  const [ack, setAck] = useState(false);
  const sur = useList('quoteSurcharge', { limit: 20 }).data?.items ?? [];
  const ext = useList('quoteContingency', { limit: 20 }).data?.items ?? [];
  const st = statusOf(q);
  const lines = viewLines(`${formatPrice(q.price)} ${q.unit}`);
  const has = hasPricingTicket(index);
  const ticket = `PR-261021-0946${String(20 + index).padStart(2, '0')}-K8`;
  const accepted = st === 'Khách chấp nhận';
  const hist: [string, string][] = [
    [`Tạo từ bảng giá ${q.carrier}`, `${dayOffset(q.madeDays)} 09:10`],
    ['Gửi email + PDF + link myHANA', `${dayOffset(q.madeDays)} 09:25`],
    st === 'Khách chấp nhận' || st === 'Đã thành Job' ? ['Khách chấp nhận trên myHANA', '22/Oct/2026 09:12'] : ['Khách mở link 2 lần', '21/Oct/2026 15:40'],
  ];
  const head = (k: Sec, title: string, icon: React.ReactNode, aria: string) => (
    <div className={s.sh}>
      <span className={s.ico}>{icon}</span>
      <h2>{title}</h2>
      <span className={s.ico} role="img" aria-label={`Giải thích: ${HINT[k]}`} title={HINT[k]}><IconInfoCircle size={15} aria-hidden="true" /></span>
      <span className={s.grow} />
      <button type="button" className={s.tog} aria-expanded={!shut[k]} aria-label={aria} title="Mở / gập" onClick={() => setShut((o) => ({ ...o, [k]: !o[k] }))}>
        <IconChevronDown size={18} aria-hidden="true" />
      </button>
    </div>
  );
  return (
    <Page kind="table">
      <PageHeader
        title={q.id}
        lead={
          <button type="button" className={s.back} aria-label="Quay lại danh sách báo giá" title="Quay lại danh sách báo giá" onClick={onBack}>
            <IconChevronLeft size={18} aria-hidden="true" />
          </button>
        }
        sub={<span>{dayOffset(q.madeDays)} · {q.sale}</span>}
        meta={<StatusChip q={q} />}
        actions={
          <>
            <div className={s.menuWrap}>
              <button type="button" className={s.tool} aria-label="Thao tác khác với báo giá" aria-haspopup="menu" aria-expanded={menu} title="Gửi lại · Gia hạn" onClick={() => setMenu((o) => !o)}>
                <IconDots size={18} aria-hidden="true" />
              </button>
              {menu && (
                <div className={s.menu} role="menu" aria-label="Thao tác khác với báo giá">
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); toast({ msg: `Đã gửi lại ${q.id} qua email + link myHANA` }); }}><IconSend size={16} aria-hidden="true" />{tx('qc.11')}</button>
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); toast({ msg: `Đã gia hạn ${q.id} thêm 7 ngày` }); }}><IconCalendarPlus size={16} aria-hidden="true" />{tx('qc.4')}</button>
                </div>
              )}
            </div>
            {accepted ? (
              <Button variant="primary" icon={<IconBriefcase size={16} aria-hidden="true" />} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>{tx('qc.12')}</Button>
            ) : (
              <Button variant="primary" icon={<IconExternalLink size={16} aria-hidden="true" />} onClick={() => void nav({ to: '/ezy/quotes/edit' })}>{tx('qc.13')}</Button>
            )}
          </>
        }
      />
      <section className={s.detail} aria-label="Chi tiết báo giá">
        <div className={s.col}>
          <section className={s.sec} aria-label="Phương án và giá">
            {head('price', 'Phương án & giá', <IconTag size={18} aria-hidden="true" />, 'Mở hoặc gập phương án và giá')}
            {!shut.price && (
              <div className={s.body}>
                <div className={s.kv}><span>Cước {q.carrier}</span><b>{formatPrice(q.price)} {q.unit}</b></div>
                <div className={s.kv}><span>Local charges (THC, Seal, Bill)</span><b>{LOCAL_PER_CONT} USD / cont</b></div>
                {q.under && <div className={s.kv}><span>{tx('qc.14')}</span><b>{tx('qc.15')}</b></div>}
              </div>
            )}
          </section>
          <section className={`${s.sec} ${s.lines}`} aria-label="Dòng báo giá" style={{ padding: '16px 18px' }}>
            <div className={s.lh}><span className={s.ico}><IconFileDollar size={18} aria-hidden="true" /></span><b>{tx('qc.16')}</b></div>
            {lines.map((l) => (
              <div key={l.code} className={s.ln}>
                <div className={s.l1}><b>{l.code}</b><span>{l.label}</span><b>{l.price}</b></div>
                <div className={s.l2}>
                  <span className={s.tag} title="Cách tính">{CALC_LABEL[l.line.calc]}</span>
                  <span className={`${s.tag} ${l.line.nature === 'svc' ? s.svc : l.line.nature === 'inc' ? s.inc : ''}`} title="Bản chất">{NATURE_LABEL[l.line.nature]}</span>
                  {l.line.scope && <span className={s.meta} title="Phạm vi">{l.line.scope}</span>}
                </div>
              </div>
            ))}
            <div className={s.sub2}><b>{tx('qc.17')}</b><span>TRFA</span></div>
            {sur.map((x) => (
              <div key={x.id} className={s.surRow}><span>{x.code && <b>{x.code}</b>}{x.code ? ' · ' : ''}{x.label}</span><span title="Công thức">{x.formula.replace('{p}', `${x.price.toLocaleString('de-DE')} ₫`)}</span></div>
            ))}
            <div className={s.sub2}><b>{tx('qc.18')}</b><span>{tx('qc.19')}</span></div>
            <div className={s.ext}>
              {ext.map((x) => (
                <span key={x.id} className={s.tag}>{x.code && <b>{x.code} </b>}{x.label}</span>
              ))}
            </div>
          </section>
          <section className={s.sec} aria-label="Hoạt động">
            {head('hist', 'Hoạt động', <IconUsers size={18} aria-hidden="true" />, 'Mở hoặc gập hoạt động')}
            {!shut.hist && (
              <div className={s.body}>
                {hist.map(([k, when]) => (
                  <div key={k} className={s.act}><i aria-hidden="true" /><div>{k}<small>{when}</small></div></div>
                ))}
              </div>
            )}
          </section>
        </div>
        <aside className={s.col} aria-label="Cột phải báo giá">
          <section className={s.sec} aria-label="Thông tin chính">
            {head('info', 'Thông tin chính', <IconFileText size={18} aria-hidden="true" />, 'Mở hoặc gập thông tin chính')}
            {!shut.info && (
              <dl className={`${s.dl} ${s.body}`}>
                <dt>{tx('qc.5')}</dt><dd>{q.customer}</dd>
                <dt>{tx('qc.20')}</dt><dd>{q.contact}</dd>
                <dt>{tx('qc.21')}</dt><dd>{q.route} · {q.transit}</dd>
                <dt>{tx('qc.22')}</dt><dd>{q.kind} · {q.qty}</dd>
                <dt>{tx('qc.23')}</dt><dd>{q.carrier} · {q.vessel}</dd>
                <dt>{tx('qc.24')}</dt><dd>{dayOffset(q.validDays)}</dd>
              </dl>
            )}
          </section>
          <section className={s.sec} aria-label="Hỏi giá">
            {head('inq', 'Hỏi giá', <IconUsers size={18} aria-hidden="true" />, 'Mở hoặc gập hỏi giá')}
            {!shut.inq && (
              <div className={s.body}>
                <div className={s.pq}>
                  <span>
                    Pricing · {has ? ticket : 'chưa hỏi'}
                    <small className={has && !ack ? s.ok : undefined}>{has ? (ack ? 'Quoted · Sale đã nhận giá' : 'Quoted · TRANGHDD đã trả giá, chờ bạn xác nhận') : 'Tuyến có giá hãng hiệu lực'}</small>
                  </span>
                  {(!has || !ack) && (
                    <Button variant="primary" icon={has ? <IconCheck size={16} aria-hidden="true" /> : undefined} onClick={() => (has ? (setAck(true), toast({ msg: `Đã xác nhận nhận giá ${ticket}` })) : toast({ msg: 'Đã gửi hỏi giá · task PRC hạn 24 giờ LV' }))}>
                      {has ? 'Đã nhận giá' : 'Hỏi Pricing'}
                    </Button>
                  )}
                </div>
                <a className={s.ncc} href="/ezy/quotes" onClick={(e) => { e.preventDefault(); toast({ msg: 'Hỏi giá đối tác chưa mở ở bản này' }); }}>
                  <span className={s.meta}><IconBuildingStore size={16} aria-hidden="true" />Đối tác / đại lý · {['Trucking', 'Hải quan', 'Air'].includes(q.kind) ? '2/4 đối tác đã chào' : 'chưa hỏi'}</span>
                  <b>{tx('qc.25')}</b>
                </a>
              </div>
            )}
          </section>
        </aside>
      </section>
    </Page>
  );
}
